import { useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { GitBranch, Database, Briefcase, XCircle } from "lucide-react";
import { adaiApi } from "../api/adaiApi.js";
import { v1ConnectionApi } from "../../api/v1ConnectionApi.js";
import { getApiError } from "../../api/client";
import { useWorkspace } from "../../context/WorkspaceContext";
import { SurfaceCard, Input, Select, Button, Alert, SkeletonText } from "../../shared/ui";

import FormField from "./ui/FormField";

const POLL_INTERVAL_MS = 5000;

const STATUS_LABEL = {
  queued: "Queued — waiting to start…",
  running: "Generating documentation…",
  completed: "Completed",
  failed: "Failed",
};

const AdaiForm = ({ onResult, onGenerationStart, onGenerationEnd, apiError, setApiError }) => {
  const { currentEngagementId, currentEngagement } = useWorkspace();

  const methods = useForm({
    defaultValues: {
      source_control_connection_id: "",
      work_item_connection_id: "",
      llm_connection_id: "",
      source_version: "",
      target_version: "",
      release_tag: "",
      release_date: "",
      date_from: "",
      date_to: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const intervalRef = useRef(null);
  const [pollingStatus, setPollingStatus] = useState(null);

  const [sourceConnections, setSourceConnections] = useState([]);
  const [workConnections, setWorkConnections] = useState([]);
  const [llmConnections, setLlmConnections] = useState([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);

  useEffect(() => {
    Promise.all([
      v1ConnectionApi.listSourceControl(),
      v1ConnectionApi.listWorkItems(),
      v1ConnectionApi.listLLMs(),
    ])
      .then(([sourceData, workData, llmData]) => {
        setSourceConnections(sourceData || []);
        setWorkConnections(workData || []);
        setLlmConnections(llmData || []);
      })
      .catch((err) => {
        setApiError("Failed to load connections. " + getApiError(err));
      })
      .finally(() => {
        setIsLoadingMetadata(false);
      });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [setApiError]);

  // No publisher_type/base_url — Confluence publishing isn't working yet,
  // so every generation just uses the backend's "file" default.
  const buildGenerateDocsPayload = (data) => {
    return {
      source_control_connection_id: data.source_control_connection_id,
      work_item_connection_id: data.work_item_connection_id,
      llm_connection_id: data.llm_connection_id || undefined,
      engagement_id: currentEngagementId,
      source_version: data.source_version.trim(),
      target_version: data.target_version.trim(),
      release_tag: data.release_tag.trim(),
      release_date: data.release_date || undefined,
      date_from: data.date_from || undefined,
      date_to: data.date_to || undefined,
    };
  };

  const requestDocumentGeneration = async (data) => {
    setApiError("");
    onResult(null);
    setPollingStatus(null);

    if (!currentEngagementId) {
      setApiError("Select a project in the switcher above before generating documentation.");
      return;
    }

    let generationStarted = false;
    let hadError = false;

    try {
      const payload = buildGenerateDocsPayload(data);

      const { data: jobData } = await adaiApi.generateDocument(payload);
      const jobId = jobData.job_id;
      setPollingStatus("queued");
      generationStarted = true;
      onGenerationStart?.();

      const finalJob = await new Promise((resolve, reject) => {
        const id = setInterval(async () => {
          try {
            const { data: job } = await adaiApi.getJobStatus(jobId);
            setPollingStatus(job.status);

            if (job.status === "completed") {
              clearInterval(id);
              intervalRef.current = null;
              resolve(job);
            } else if (job.status === "failed") {
              clearInterval(id);
              intervalRef.current = null;
              reject(new Error(job.error_message || "Document generation failed."));
            }
          } catch (err) {
            clearInterval(id);
            intervalRef.current = null;
            reject(err);
          }
        }, POLL_INTERVAL_MS);

        intervalRef.current = id;
      });

      onResult(finalJob);
    } catch (err) {
      hadError = true;
      setApiError(getApiError(err));
    } finally {
      setPollingStatus(null);
      onGenerationEnd?.(generationStarted && hadError);
    }
  };

  const buttonLabel = isSubmitting
    ? (STATUS_LABEL[pollingStatus] ?? "Starting…")
    : "Generate Documentation";

  if (isLoadingMetadata) {
    return (
      <SurfaceCard className="mb-5" padding="p-6">
        <SkeletonText lines={9} />
      </SurfaceCard>
    );
  }

  const sourceOptions = sourceConnections.map((c) => ({ value: c.id || c.connection_id, label: `${c.name} (${c.provider})` }));
  const workOptions = workConnections.map((c) => ({ value: c.id || c.connection_id, label: `${c.name} (${c.provider})` }));
  const llmOptions = llmConnections.map((c) => ({ value: c.id || c.connection_id, label: `${c.name} (${c.provider})` }));

  return (
    <div className="mb-5">
      <h3 className="text-lg font-bold text-ink mb-4">Generate Release Documentation</h3>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(requestDocumentGeneration)} className="flex flex-col gap-5">
          <SurfaceCard padding="p-6">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-line">
              <div className="bg-brand-50 p-2.5 rounded-xl">
                <Database className="text-brand-700" size={20} />
              </div>
              <div>
                <h4 className="text-base font-bold text-ink m-0">Data Sources</h4>
                <p className="text-sm text-muted m-0 mt-0.5">
                  Select your registered connections. Generating against:{" "}
                  <strong>{currentEngagement?.client_name || "no project selected"}</strong>.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <FormField label="Source Control Connection" error={errors.source_control_connection_id} required>
                <Select
                  {...register("source_control_connection_id", { required: "Source Control Connection is required" })}
                  options={[{ value: "", label: "-- Select Source Control --" }, ...sourceOptions]}
                />
              </FormField>

              <FormField label="Work Item Connection" error={errors.work_item_connection_id} required>
                <Select
                  {...register("work_item_connection_id", { required: "Work Item Connection is required" })}
                  options={[{ value: "", label: "-- Select Work Item DB --" }, ...workOptions]}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="LLM Connection (Optional)" error={errors.llm_connection_id}>
                <Select
                  {...register("llm_connection_id")}
                  options={[{ value: "", label: "-- Use platform default --" }, ...llmOptions]}
                />
              </FormField>
            </div>
          </SurfaceCard>

          <SurfaceCard padding="p-6">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-line">
              <div className="bg-brand-50 p-2.5 rounded-xl">
                <Briefcase className="text-brand-700" size={20} />
              </div>
              <div>
                <h4 className="text-base font-bold text-ink m-0">Release Scope</h4>
                <p className="text-sm text-muted m-0 mt-0.5">
                  Define the versions and tags for this documentation run.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <FormField label="Source Version" error={errors.source_version} required>
                <Input {...register("source_version", { required: "Source Version is required" })} placeholder="e.g. main" />
              </FormField>
              <FormField label="Target Version" error={errors.target_version} required>
                <Input {...register("target_version", { required: "Target Version is required" })} placeholder="e.g. release/v2.1.0" />
              </FormField>
            </div>

            <FormField label="Release Tag / Sprint" error={errors.release_tag} required>
              <Input {...register("release_tag", { required: "Release Tag is required" })} placeholder="e.g. Sprint 12" />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 mb-4">
              <FormField label="Date From (Optional)" error={errors.date_from}>
                <Input type="date" {...register("date_from")} />
              </FormField>
              <FormField label="Date To (Optional)" error={errors.date_to}>
                <Input type="date" {...register("date_to")} />
              </FormField>
            </div>

            <FormField label="Documentation Release Date (Optional)">
              <Input type="date" {...register("release_date")} />
            </FormField>
          </SurfaceCard>

          {apiError && (
            <Alert variant="danger" icon={XCircle} title="Couldn't generate documentation">
              {apiError}
            </Alert>
          )}

          <div className="flex justify-end border-t border-line pt-4">
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={isLoadingMetadata}>
              <GitBranch size={16} />
              {buttonLabel}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AdaiForm;
